import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

// ALWAYS use relative URL - same-origin requests only
const API = '/api';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const requestUrl = `${API}/auth/login`;
    console.log('[Login] Request URL:', requestUrl);

    try {
      const response = await axios.post(
        requestUrl,
        { email, password },
        { withCredentials: true }
      );
      
      console.log('[Login] Success:', response.status);
      toast.success('Login successful!');
      navigate('/dashboard', { state: { user: response.data } });
    } catch (err) {
      console.error('[Login] Error:', err);
      console.error('[Login] Request URL was:', requestUrl);
      
      // Enhanced error handling with detailed messages
      let errorMessage = 'Login failed';
      let errorCode = 'AUTH_ERR_UNKNOWN';
      let suggestion = 'Please try again or contact support.';

      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        errorCode = `AUTH_ERR_${status}`;

        if (status === 401) {
          errorMessage = detail || 'Invalid email or password';
          suggestion = 'Please check your credentials and try again.';
        } else if (status === 400) {
          errorMessage = detail || 'Invalid request';
          suggestion = 'Please fill in all required fields.';
        } else if (status === 429) {
          errorMessage = 'Too many login attempts';
          suggestion = 'Please wait a few minutes before trying again.';
        } else if (status >= 500) {
          errorMessage = 'Server error';
          suggestion = 'Our servers are having issues. Please try again later.';
        } else {
          errorMessage = detail || `Error (${status})`;
        }
      } else if (err.request) {
        errorCode = 'AUTH_ERR_NETWORK';
        errorMessage = 'Unable to connect to server';
        suggestion = `Request failed. URL: ${requestUrl}. Check browser console for details.`;
        console.error('[Login] No response received. Possible CORS or network issue.');
      } else {
        errorCode = 'AUTH_ERR_SETUP';
        errorMessage = err.message || 'Request setup failed';
        suggestion = 'An unexpected error occurred. Please try again.';
      }

      setError({ message: errorMessage, code: errorCode, suggestion });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-4">
      <Card className="w-full max-w-md" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading">Welcome Back</CardTitle>
          <CardDescription>Sign in to your ScanMyAd account</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Error Banner */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg" data-testid="login-error-banner">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">{error.message}</p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error.suggestion}</p>
                    <p className="text-xs text-red-500 dark:text-red-500 mt-1 font-mono">Code: {error.code}</p>
                  </div>
                </div>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link 
                to="/forgot-password" 
                className="text-sm text-primary hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary hover:underline" data-testid="goto-register-link">
              Sign up
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
